/**
 * useApiClient Hook (DC-1370)
 *
 * Centralized API client with authentication, user context headers,
 * GET response caching, and in-flight request deduplication.
 *
 * Features:
 * - Automatic JWT token attachment and 401 refresh
 * - Multi-tenant headers (X-User-Id, X-Org-Id)
 * - GET response caching with configurable TTL (default 5 minutes)
 * - In-flight request deduplication
 * - Automatic cache invalidation on mutations (POST/PUT/PATCH/DELETE)
 */

import { useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  getCacheKey,
  getCachedResponse,
  setCachedResponse,
  getInFlightRequest,
  setInFlightRequest,
  invalidateCache,
  invalidateRelatedCaches,
} from './apiCache';

// ============================================================================
// TYPES
// ============================================================================

const DEFAULT_CACHE_TTL = 5 * 60 * 1000; // 5 minutes

/**
 * A failed request, carrying the server's PARSED error body (DC-1830).
 *
 * The standardized error envelope is `{ error: { code, message, details } }`,
 * and `details` is where a route names WHICH field it refused and why. Before
 * this, only `message` survived the transport, so a per-field refusal — a
 * collector binding whose parameters fail their template's schema, say — could
 * not be shown against the field that caused it.
 *
 * Additive and backward-compatible on purpose: this still IS an `Error` with
 * the same `.message` every existing caller reads, so a caller that does not
 * know about `body` is unaffected. `body` is `unknown` because it is the
 * server's shape, not this hook's; read it defensively.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly body: unknown;

  constructor(message: string, status: number, body: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
  }
}

export interface ApiClientOptions extends Omit<RequestInit, 'headers'> {
  headers?: Record<string, string>;
  /** Cache TTL in ms for GET requests. Set to 0 to disable caching. Default: 5 minutes. */
  cacheTtl?: number;
  /** Skip cache and force a fresh request (still updates cache with new response). */
  skipCache?: boolean;
}

export interface ApiClient {
  /** Make an authenticated GET request (cached by default) */
  get: <T = unknown>(url: string, options?: ApiClientOptions) => Promise<T>;
  /** Make an authenticated POST request (invalidates related caches) */
  post: <T = unknown>(url: string, body?: unknown, options?: ApiClientOptions) => Promise<T>;
  /** Make an authenticated PUT request (invalidates related caches) */
  put: <T = unknown>(url: string, body?: unknown, options?: ApiClientOptions) => Promise<T>;
  /** Make an authenticated PATCH request (invalidates related caches) */
  patch: <T = unknown>(url: string, body?: unknown, options?: ApiClientOptions) => Promise<T>;
  /** Make an authenticated DELETE request (invalidates related caches) */
  delete: <T = unknown>(url: string, options?: ApiClientOptions) => Promise<T>;
  /** Raw fetch with authentication - returns Response object (no caching) */
  authFetch: (url: string, options?: RequestInit) => Promise<Response>;
  /** Invalidate cached GET responses matching a pattern */
  invalidateCache: (pattern?: string | RegExp) => void;
  /** Get the current user ID (or null if not authenticated) */
  userId: string | null;
  /** Get the current organization ID (or null if not authenticated) */
  orgId: string | null;
  /** Check if user is authenticated */
  isAuthenticated: boolean;
}

// ============================================================================
// HOOK
// ============================================================================

/**
 * Hook to make authenticated API requests with automatic user context.
 *
 * Usage:
 * ```tsx
 * const api = useApiClient();
 *
 * // GET with caching (default 5 min TTL)
 * const data = await api.get('/api/journeys');
 *
 * // GET with custom TTL
 * const data = await api.get('/api/journeys', { cacheTtl: 30000 }); // 30s
 *
 * // GET with no caching
 * const data = await api.get('/api/journeys', { skipCache: true });
 *
 * // POST (auto-invalidates /api/journeys cache)
 * const result = await api.post('/api/journeys', { name: 'New Journey' });
 *
 * // Manual cache invalidation
 * api.invalidateCache('/api/journeys');
 * ```
 */
export function useApiClient(): ApiClient {
  const { user, accessToken, isAuthenticated, refreshToken, logout } = useAuth();

  const userId = user?.id ?? null;
  const orgId = user?.organization?.id ?? null;

  /**
   * Make an authenticated fetch request with automatic headers
   */
  const authFetch = useCallback(
    async (url: string, options: RequestInit = {}): Promise<Response> => {
      const headers = new Headers(options.headers);

      // Add Authorization header if we have a token
      if (accessToken) {
        headers.set('Authorization', `Bearer ${accessToken}`);
      }

      // Add user context headers (required for multi-tenant data isolation)
      if (userId) {
        headers.set('X-User-Id', userId);
      }
      if (orgId) {
        headers.set('X-Org-Id', orgId);
      }

      let response = await fetch(url, {
        ...options,
        headers,
        credentials: 'include',
      });

      // If 401, try to refresh token and retry once
      if (response.status === 401 && refreshToken) {
        const refreshed = await refreshToken();
        if (refreshed) {
          // Retry with refreshed token
          const retryHeaders = new Headers(options.headers);
          if (accessToken) {
            retryHeaders.set('Authorization', `Bearer ${accessToken}`);
          }
          if (userId) {
            retryHeaders.set('X-User-Id', userId);
          }
          if (orgId) {
            retryHeaders.set('X-Org-Id', orgId);
          }
          response = await fetch(url, {
            ...options,
            headers: retryHeaders,
            credentials: 'include',
          });
        } else {
          // Refresh failed, logout
          await logout();
        }
      }

      return response;
    },
    [accessToken, userId, orgId, refreshToken, logout]
  );

  /**
   * Helper to make JSON requests and parse response.
   * GET requests are cached by default; mutations invalidate related caches.
   */
  const jsonRequest = useCallback(
    async <T = unknown>(
      method: string,
      url: string,
      body?: unknown,
      options: ApiClientOptions = {}
    ): Promise<T> => {
      const isGet = method === 'GET';
      const ttl = options.cacheTtl ?? DEFAULT_CACHE_TTL;
      const useCache = isGet && ttl > 0 && !options.skipCache;

      // GET: Check response cache first
      if (useCache) {
        const cacheKey = getCacheKey(url);
        const cached = getCachedResponse<T>(cacheKey);
        if (cached !== undefined) {
          return cached;
        }

        // Check for in-flight deduplication
        const inFlight = getInFlightRequest<T>(cacheKey);
        if (inFlight) {
          return inFlight;
        }
      }

      const headers: Record<string, string> = {
        ...options.headers,
      };

      // Add Content-Type for requests with body
      if (body !== undefined) {
        headers['Content-Type'] = 'application/json';
      }

      const requestPromise = (async (): Promise<T> => {
        const response = await authFetch(url, {
          ...options,
          method,
          headers,
          body: body !== undefined ? JSON.stringify(body) : undefined,
        });

        if (!response.ok) {
          const errorText = await response.text();
          let errorMessage: string;
          let errorBody: unknown;
          try {
            const errorJson = JSON.parse(errorText);
            errorBody = errorJson;
            errorMessage = errorJson.error?.message || errorJson.message || errorText;
          } catch {
            errorMessage = errorText || `HTTP ${response.status}`;
          }
          throw new ApiError(errorMessage, response.status, errorBody);
        }

        // Handle empty responses
        const text = await response.text();
        if (!text) {
          return {} as T;
        }

        return JSON.parse(text) as T;
      })();

      // GET: Register in-flight and cache on success
      if (useCache) {
        const cacheKey = getCacheKey(url);
        const deduped = setInFlightRequest(cacheKey, requestPromise);

        try {
          const result = await deduped;
          setCachedResponse(cacheKey, result, ttl);
          return result;
        } catch (error) {
          // Don't cache errors
          throw error;
        }
      }

      // Mutations: Invalidate related caches after success
      if (!isGet) {
        const result = await requestPromise;
        invalidateRelatedCaches(url);
        return result;
      }

      return requestPromise;
    },
    [authFetch]
  );

  const get = useCallback(
    <T = unknown>(url: string, options?: ApiClientOptions): Promise<T> => {
      return jsonRequest<T>('GET', url, undefined, options);
    },
    [jsonRequest]
  );

  const post = useCallback(
    <T = unknown>(url: string, body?: unknown, options?: ApiClientOptions): Promise<T> => {
      return jsonRequest<T>('POST', url, body, options);
    },
    [jsonRequest]
  );

  const put = useCallback(
    <T = unknown>(url: string, body?: unknown, options?: ApiClientOptions): Promise<T> => {
      return jsonRequest<T>('PUT', url, body, options);
    },
    [jsonRequest]
  );

  const patch = useCallback(
    <T = unknown>(url: string, body?: unknown, options?: ApiClientOptions): Promise<T> => {
      return jsonRequest<T>('PATCH', url, body, options);
    },
    [jsonRequest]
  );

  const del = useCallback(
    <T = unknown>(url: string, options?: ApiClientOptions): Promise<T> => {
      return jsonRequest<T>('DELETE', url, undefined, options);
    },
    [jsonRequest]
  );

  return {
    get,
    post,
    put,
    patch,
    delete: del,
    authFetch,
    invalidateCache,
    userId,
    orgId,
    isAuthenticated,
  };
}

export default useApiClient;
